import React from 'react';
import { Button, HTMLSelect } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { AxisSelection, AxisTrace, TIME_AXIS, TraceAxes } from './signalAxes';

interface AxisSelectorProps<K extends string> {
  axes: K[];
  options: string[];
  selection: AxisSelection<K>;
  onChange: (selection: AxisSelection<K>) => void;
  traces: AxisTrace<K>[];
}

// A new trace repeats the last one with the next signal on its last axis, so it
// shows something new right away.
const getNextTrace = <K extends string>(last: TraceAxes<K>, axes: K[], options: string[]): TraceAxes<K> => {
  const lastAxis = axes[axes.length - 1];
  const signals = options.filter((option) => option !== TIME_AXIS);
  const next = signals[(signals.indexOf(last[lastAxis]) + 1) % signals.length] ?? last[lastAxis];

  return { ...last, [lastAxis]: next };
};

// Takes the legend's place in the side pane for the 3D and player views: each
// trace pairs one variable per axis.
export const AxisSelector = <K extends string>({ axes, options, selection, onChange, traces }: AxisSelectorProps<K>) => {
  const setAxis = (traceIndex: number, axis: K, value: string) => {
    onChange(selection.map((trace, index) => (index === traceIndex ? { ...trace, [axis]: value } : trace)));
  };

  const removeTrace = (traceIndex: number) => {
    onChange(selection.filter((_, index) => index !== traceIndex));
  };

  const addTrace = () => {
    onChange([...selection, getNextTrace(selection[selection.length - 1], axes, options)]);
  };

  return (
    <div className="cm-legend">
      <h2 className="cm-section-title">Traces</h2>
      <ul className="cm-axis-traces">
        {selection.map((trace, traceIndex) => (
          // Traces have no identity beyond their position.
          <li key={traceIndex} className="cm-axis-trace">
            <div className="cm-axis-trace-header">
              <span
                className={`cm-legend-swatch is-${traces[traceIndex]?.dash ?? 'solid'}`}
                style={{ color: traces[traceIndex]?.color }}
              />
              <span className="cm-axis-label">Trace {traceIndex + 1}</span>
              <Button
                small
                minimal
                icon={IconNames.Cross}
                aria-label={`Remove trace ${traceIndex + 1}`}
                // There is always at least one trace.
                disabled={selection.length <= 1}
                onClick={() => removeTrace(traceIndex)}
              />
            </div>
            {axes.map((axis) => (
              <label key={axis} className="cm-axis-row">
                <span className="cm-axis-label">{axis.toUpperCase()}</span>
                <HTMLSelect
                  fill
                  value={trace[axis]}
                  options={options}
                  onChange={(e) => setAxis(traceIndex, axis, e.target.value)}
                />
              </label>
            ))}
          </li>
        ))}
      </ul>
      <Button small icon={IconNames.Plus} onClick={addTrace}>Add trace</Button>
    </div>
  );
};
